/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { Badge } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import {
  formatCurrency,
  formatUnit,
  progressBarCheck,
  utilityCostCkeck,
  progressSavingCheck,
  sizeCheck,
  savingPercentageCheck,
  utilityCostCurrencyCheck
} from '../../shared/Utils/Utils';
import CommonProgressBar from '../CommonProgressBar/CommonProgressBar';
import { IProgressBarWidgetProps, scaledData } from './IProgressBarWidget';
import './ProgressBarWidgetTheme.scss';
import { KPI_PERCENTAGE_DECIMAL_PRECISION } from '../Utils/Constants';
import {
  getNullToZero,
  getProgressBarActualValues,
  getProgressBarBaselineValues
} from '../Utils/EnergyUsagesViewDetailsUtils';
import { getUpdatedProgressBarWidth } from '../CommonProgressBar/CommonProgressBarUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { ApiContext } from '../../../context/ApiContext';

const getEnergyUsageClassName = (
  isVMEnergyUsage: boolean | undefined,
  savingValue: string
) => {
  switch (savingValue) {
    case 'saving-value-container':
      return isVMEnergyUsage
        ? 'saving-value-container'
        : 'saving-value-container container-font';
    case 'unit-vm':
      return isVMEnergyUsage ? 'unit-vm' : 'unit';
    case 'saving-header-vm':
      return isVMEnergyUsage ? 'saving-header-vm' : 'saving-header';
    default:
      return '';
  }
};

const getBaseLineClassName = (
  isVMEnergyUsage: boolean | undefined,
  savingValue: string
) => {
  switch (savingValue) {
    case 'baseline-value-vm':
      return isVMEnergyUsage ? 'baseline-value-vm' : 'baseline-value';
    case 'baseline-heading-vm':
      return isVMEnergyUsage ? 'baseline-heading-vm' : 'baseline-heading';
    default:
      return '';
  }
};

const displayBaselineCheck = (
  displayBaseline: boolean | undefined,
  t: IAny,
  d: IAny,
  previousName: boolean | undefined
) =>
  displayBaseline
    ? t(d.BASELINE)
    : previousName
    ? t(d.PREVIOUS)
    : t(d.PREVIOUSPERIOD);

const unitFormatCheck = (
  utilityCostCurrency: string | undefined,
  actual: number | null
) =>
  utilityCostCurrency && actual !== null
    ? formatUnit(formatCurrency(actual, utilityCostCurrency))
    : '';

// const getPercentageDisplay = (isVMEnergyUsage: boolean | undefined )=> {
//   return isVMEnergyUsage ? (
//     <div className="VM-PercentageDisplay">
//       {percentageDisplay(
//         savingsPercentage,
//         savings,
//         utilityCostCurrency,
//         progressBarSavingsPercentage,
//         unit
//       )}
//     </div>
//   ) : (
//     <></>
//   )
// }

const getDeviationScaledData = (
  savings: number,
  unit: {} | null | undefined,
  utilityCostCurrency: string | undefined,
  deviationScaledData?: scaledData,
  toFixedValue?: number,
  fromCarbonKPI?: boolean,
  changeUnit?: string,
  originalUnit?: string,
  conversionType?: string
) => {
  return savings === 0 &&
    deviationScaledData &&
    deviationScaledData.Value! !== 0
    ? `${Math.abs(deviationScaledData?.Value!)} ${deviationScaledData?.Unit}`
    : `${utilityCostCurrencyCheck(
        utilityCostCurrency,
        savings,
        toFixedValue,
        fromCarbonKPI,
        changeUnit,
        originalUnit,
        conversionType
      )} ${renderUnit(fromCarbonKPI ? changeUnit : unit)}`;
};
const renderUnit = (unit: {} | null | undefined) => {
  return unit ? unit : '';
};
const progressBarWidget = (props: IProgressBarWidgetProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const emaasContext = useContext(ApiContext);
  const { previousName } = emaasContext!;
  const {
    aggregationData: {
      actual,
      previousPeriod,
      savings,
      savingsPercentage,
      absoluteActual,
      absolutePrevPeriod,
      actualUnit,
      baselineUnit,
      changeUnit,
      originalActual,
      originalPreviousPeriod,
      originalSavings
    },
    unit,
    category,
    utilityCostCurrency,
    isProgressBarForEnergy,
    isVMEnergyUsage,
    deviationScaledData,
    basicSub,
    displayBaseline,
    toFixedValue,
    fromCarbonKPI
  } = props;
  const progressBarActual: IAny = getProgressBarActualValues(
    utilityCostCurrency,
    actual,
    toFixedValue,
    fromCarbonKPI,
    actualUnit,
    props.originalUnit,
    props.conversionType
  );
  const progressBarBaseline = getProgressBarBaselineValues(
    utilityCostCurrency,
    previousPeriod,
    toFixedValue,
    props.originalUnit,
    baselineUnit,
    props.conversionType
  );
  const progressBarSavingsPercentage = Math.abs(
    getNullToZero(savingsPercentage)
  ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION);

  const actualLength = progressBarCheck(progressBarActual);
  const unitLength = utilityCostCkeck(utilityCostCurrency!, unit!);
  const savingsLength = progressSavingCheck(
    savings,
    progressBarSavingsPercentage
  );

  const { actualWidth, prevWidth } = getUpdatedProgressBarWidth(
    actual,
    previousPeriod,
    absoluteActual!,
    absolutePrevPeriod!,
    isProgressBarForEnergy,
    props.fromCarbonKPI
  );

  const percentageDisplay = (
    savingsPercentageVal: number | null,
    savingsVal: number | null,
    utilityCostCurrencyVal: string | undefined,
    progressBarSavingsPercentageVal: {} | null | undefined,
    unitVal: {} | null | undefined,
    fromCarbonKPI?: boolean,
    changeUnit?: string,
    originalUnit?: string,
    conversionType?: string
  ) => {
    return savingsVal === null ? (
      <span className="dash">--</span>
    ) : (
      <div className="percentage">
        <span
          className={`${
            savingsPercentageVal! > 0 ||
            (savingsPercentageVal === 0 && savingsVal === 0)
              ? 'saving-percentage-positive'
              : 'saving-percentage-negative'
          }`}
        >
          {' '}
          <Badge
            color={savingPercentageCheck(savingsPercentageVal, savingsVal)}
            empty
          />
          {getDeviationScaledData(
            savingsVal,
            unitVal,
            utilityCostCurrencyVal,
            deviationScaledData,
            toFixedValue,
            fromCarbonKPI,
            changeUnit,
            originalUnit,
            conversionType
          )}{' '}
          {/* {savings === 0 &&
          deviationScaledData &&
          deviationScaledData?.Value! !== 0
            ? `${Math.abs(deviationScaledData?.Value!)} ${
                deviationScaledData?.Unit
              }`
            : `${utilityCostCurrencyCheck(utilityCostCurrency, savings)} ${
                unit ? unit : ''
              }`}{' '} */}
          ({progressBarSavingsPercentageVal}
          %)
        </span>
      </div>
    );
  };
  return (
    <div
      className={`${
        props.optimusSubscribed
          ? 'progressbarM-container-optimus'
          : 'progressbarM-container'
      }`}
    >
      <div className="first-row">
        <div
          className={`saving-container ${sizeCheck(
            actualLength,
            unitLength,
            savingsLength
          )} ${actualLength + unitLength + savingsLength}`}
        >
          <div
            // className={
            //   isVMEnergyUsage
            //     ? 'saving-value-container'
            //     : 'saving-value-container container-font'
            // }
            className={getEnergyUsageClassName(
              isVMEnergyUsage,
              'saving-value-container'
            )}
          >
            <div className="saving-with-unit-container">
              <span className="saving-value"> {progressBarActual} </span>
              <span className="unit">
                {/* {utilityCostCurrency !== 'MYR' &&
                  unitFormatCheck(utilityCostCurrency, actual)} */}
              </span>
              <span
                // className={isVMEnergyUsage ? 'unit-vm' : 'unit'}
                className={getEnergyUsageClassName(isVMEnergyUsage, 'unit-vm')}
              >
                {fromCarbonKPI ? actualUnit : !actual ? '' : unit}
              </span>
            </div>

            {!isVMEnergyUsage ? (
              percentageDisplay(
                savingsPercentage,
                savings,
                utilityCostCurrency,
                progressBarSavingsPercentage,
                unit,
                props.fromCarbonKPI,
                changeUnit,
                props.originalUnit,
                props.conversionType
              )
            ) : (
              <></>
            )}
          </div>
          <span
            // className={isVMEnergyUsage ? 'saving-header-vm' : 'saving-header'}
            className={getEnergyUsageClassName(
              isVMEnergyUsage,
              'saving-header-vm'
            )}
          >
            {props.renderedFromCarbon ? t(d.ACTUALEMISSIONS) : t(d.ACTUAL)}
          </span>
          {isVMEnergyUsage ? (
            <div className="VM-PercentageDisplay">
              {percentageDisplay(
                savingsPercentage,
                savings,
                utilityCostCurrency,
                progressBarSavingsPercentage,
                unit,
                props.fromCarbonKPI,
                changeUnit,
                props.originalUnit,
                props.conversionType
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className="bottom-row">
        {console.log('savingssavingssavings in CommonProgressBar', savings)}
        <div>
          <CommonProgressBar
            actual={actualWidth}
            previousPeriod={prevWidth}
            savings={savings}
            savingsPercentage={savingsPercentage}
            category={category}
          />
        </div>
        <div className="last-row-carbon-kpi">
          <div className="summary-baseline-right">
            {
              <span
                // className={
                //   isVMEnergyUsage ? 'baseline-value-vm' : 'baseline-value'
                // }
                className={getBaseLineClassName(
                  isVMEnergyUsage,
                  'baseline-value-vm'
                )}
              >
                {progressBarBaseline}{' '}
                {fromCarbonKPI ? baselineUnit : !previousPeriod ? ' ' : unit}
              </span>
            }

            {category === 'EnergyUsage' || category === 'EnergyUsageNILM' ? (
              <div
                // className={
                //   isVMEnergyUsage ? 'baseline-heading-vm' : 'baseline-heading'
                // }
                className={getBaseLineClassName(
                  isVMEnergyUsage,
                  'baseline-heading-vm'
                )}
              >
                {basicSub === 'basicSub' || basicSub === true
                  ? t(d.PREVIOUSPERIOD)
                  : t(d.BASELINE)}
              </div>
            ) : (
              <div className="baseline-heading">
                {props.renderedFromCarbon
                  ? t(d.PREVIOUSEMISSIONS)
                  : displayBaselineCheck(displayBaseline, t, d, previousName)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default progressBarWidget;
